/**
 * Timezone utility functions for displaying UTC timestamps in user's local timezone.
 * 
 * Important: Timestamps from the backend may be naive (no timezone info).
 * We treat them as UTC and convert to the user's local timezone for display.
 */

export function formatLocalTime(utcTimestamp, options = {}) {
  if (!utcTimestamp) return 'N/A';

  try {
    let date;
    const timestampStr = String(utcTimestamp);
    
    // Check if timestamp has timezone info (contains +, -, or Z at the end)
    const hasTimezone = /[+-]\d{2}:\d{2}$|Z$/.test(timestampStr);
    
    if (hasTimezone) {
      // Timestamp has timezone info, parse normally
      date = new Date(utcTimestamp);
    } else {
      // Naive timestamp (no timezone), treat as UTC by appending 'Z'
      // This ensures "2026-02-28T03:49:40.290" is treated as UTC, not local time
      date = new Date(timestampStr + 'Z');
    }
    
    if (isNaN(date.getTime())) return 'Invalid Date';

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options,
    };

    return date.toLocaleString(undefined, defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Error';
  }
}

export function formatLocalDate(utcTimestamp) {
  return formatLocalTime(utcTimestamp, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: undefined,
    minute: undefined,
  });
}

export function formatLocalTimeOnly(utcTimestamp) {
  return formatLocalTime(utcTimestamp, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    year: undefined,
    month: undefined,
    day: undefined,
  });
}

export function formatRelativeTime(utcTimestamp) {
  if (!utcTimestamp) return 'N/A';

  try {
    const date = new Date(utcTimestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatLocalDate(utcTimestamp);
  } catch (error) {
    return formatLocalDate(utcTimestamp);
  }
}

export function getUserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
